import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI, tokenStore } from '../../services/api';

export interface User {
  id: string;
  email: string;
  name: string;
  firstName?: string;
  lastName?: string;
  phoneNumber: string;
  profileImage?: string;
  isEmailVerified: boolean;
  isPhoneVerified: boolean;
  is2FAEnabled: boolean;
  createdAt: string;
  updatedAt?: string;
}

export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  tokens: {
    accessToken: string | null;
    refreshToken: string | null;
  };
  /** Set when the password was accepted but an authenticator code is required. */
  twoFactorChallengeToken: string | null;
}

const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  twoFactorChallengeToken: null,
  error: null,
  tokens: {
    accessToken: null,
    refreshToken: null,
  },
};

// Async thunks
export const initializeAuth = createAsyncThunk(
  'auth/initialize',
  async (_, { rejectWithValue }) => {
    try {
      const accessToken = tokenStore.getAccessToken();

      if (!accessToken) {
        return { isAuthenticated: false, user: null };
      }

      // Validate the token with the server and fetch user data
      try {
        const response = await authAPI.checkProfileCompletion();
        const user = response.data?.user || null;
        return { isAuthenticated: true, user };
      } catch {
        // Token is invalid or expired - try refresh
        const refreshToken = tokenStore.getRefreshToken();
        if (refreshToken) {
          try {
            const refreshResponse = await authAPI.refreshToken(refreshToken);
            if (refreshResponse.data?.accessToken) {
              tokenStore.setAccessToken(refreshResponse.data.accessToken);
              // Retry fetching user data with new token
              const response = await authAPI.checkProfileCompletion();
              const user = response.data?.user || null;
              return { isAuthenticated: true, user };
            }
          } catch {
            // Refresh also failed
          }
        }
        tokenStore.clear();
        return { isAuthenticated: false, user: null };
      }
    } catch (error: any) {
      return rejectWithValue('Failed to initialize auth');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: { email: string; password: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Login failed');
    }
  }
);

export const login2FA = createAsyncThunk(
  'auth/login2FA',
  async (payload: { challengeToken: string; code: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.login2FA(payload);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Invalid code');
    }
  }
);

export const register = createAsyncThunk(
  'auth/register',
  async (userData: {
    name: string;
    email: string;
    phoneNumber: string;
    password: string;
    confirmPassword: string;
  }, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Registration failed');
    }
  }
);

export const verifyEmail = createAsyncThunk(
  'auth/verifyEmail',
  async (token: string, { rejectWithValue }) => {
    try {
      const response = await authAPI.verifyEmail(token);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Email verification failed');
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    // Clear tokens FIRST so the 401 interceptor doesn't re-authenticate
    tokenStore.clear();
    try {
      await authAPI.logout();
      return true;
    } catch (error: any) {
      // Even if the server call fails, we've already cleared local tokens
      return rejectWithValue(error.response?.data?.error?.message || 'Logout failed');
    }
  }
);

export const checkProfileCompletion = createAsyncThunk(
  'auth/checkProfileCompletion',
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.checkProfileCompletion();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error?.message || 'Profile check failed');
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    cancelTwoFactor: (state) => {
      state.twoFactorChallengeToken = null;
      state.error = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    setTokens: (state, action: PayloadAction<{ accessToken: string; refreshToken: string }>) => {
      state.tokens = action.payload;
      state.isAuthenticated = true;
    },
    clearAuth: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.tokens = { accessToken: null, refreshToken: null };
      state.error = null;
    },
    updateUserField: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Initialize Auth
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isAuthenticated = action.payload.isAuthenticated;
        if (action.payload.isAuthenticated && action.payload.user) {
          state.user = action.payload.user;
        }
        if (!action.payload.isAuthenticated) {
          state.user = null;
          state.tokens = { accessToken: null, refreshToken: null };
          tokenStore.clear();
        }
      })
      .addCase(initializeAuth.rejected, (state) => {
        state.isLoading = false;
        state.isAuthenticated = false;
        state.tokens = { accessToken: null, refreshToken: null };
        tokenStore.clear();
      })
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        if (action.payload.requires2FA) {
          // Password accepted; wait for the authenticator code.
          state.twoFactorChallengeToken = action.payload.challengeToken;
          return;
        }
        state.twoFactorChallengeToken = null;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        // Update in-memory token store for axios interceptor
        if (action.payload.tokens?.accessToken) {
          tokenStore.setTokens(action.payload.tokens.accessToken, action.payload.tokens.refreshToken || '');
        }
      })
      .addCase(login2FA.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login2FA.fulfilled, (state, action) => {
        state.isLoading = false;
        state.twoFactorChallengeToken = null;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.error = null;
        if (action.payload.tokens?.accessToken) {
          tokenStore.setTokens(action.payload.tokens.accessToken, action.payload.tokens.refreshToken || '');
        }
      })
      .addCase(login2FA.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Register
      .addCase(register.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.isLoading = false;
        state.user = action.payload.user;
        state.tokens = action.payload.tokens;
        state.isAuthenticated = true;
        state.error = null;
        // Update in-memory token store for axios interceptor
        if (action.payload.tokens?.accessToken) {
          tokenStore.setTokens(action.payload.tokens.accessToken, action.payload.tokens.refreshToken || '');
        }
      })
      .addCase(register.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Verify Email
      .addCase(verifyEmail.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(verifyEmail.fulfilled, (state, action) => {
        state.isLoading = false;
        if (state.user) {
          state.user.isEmailVerified = true;
        }
        state.error = null;
      })
      .addCase(verifyEmail.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Logout
      .addCase(logout.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(logout.fulfilled, (state) => {
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.tokens = { accessToken: null, refreshToken: null };
        state.error = null;
      })
      .addCase(logout.rejected, (state) => {
        // Always clear auth state on logout, even if the server call failed
        // (tokens were already cleared in the thunk before the API call)
        state.isLoading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.tokens = { accessToken: null, refreshToken: null };
        state.error = null;
      })
      // Check Profile Completion
      .addCase(checkProfileCompletion.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(checkProfileCompletion.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
      })
      .addCase(checkProfileCompletion.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, setTokens, clearAuth, updateUserField, cancelTwoFactor } = authSlice.actions;
export default authSlice.reducer;
